import type { DgContent } from './artifacts.ts';

/**
 * Remesh's per-message-type duration heuristic, in seconds, for the two question types a
 * Sigora guide can contain. There is no measurement behind them — they are constants a
 * participant's time is assumed against, and the price is assumed with it.
 */
export const ESTIMATED_SECONDS_PER_ASK = 90;
export const ESTIMATED_SECONDS_PER_POLL = 10;

/**
 * An image shown before a question, at Remesh's own estimate for an image message, so the price
 * Sigora quotes counts it as Remesh does. A placeholder business value.
 */
export const ESTIMATED_SECONDS_PER_IMAGE = 10;

/**
 * A screener poll on a recruit flex study is estimated higher than an ordinary poll,
 * because it is answered before the study starts rather than in flow.
 */
export const ESTIMATED_SECONDS_PER_SCREENER_POLL = 15;

/**
 * Every recruit study's estimate is inflated by a tenth before it is priced. It is a
 * cushion, and leaving it out would under-quote every study by 10%.
 */
export const RECRUIT_DURATION_BUFFER = 1.1;

/**
 * What one open question, one poll and one image shown add to a guide's estimate, in seconds, with
 * the recruit buffer and unrounded: the per-item form of `estimateGuideDurationSeconds`, for the
 * prompts that tell a model what a question costs.
 */
export const GUIDE_SECONDS_PER = {
  open: ESTIMATED_SECONDS_PER_ASK * RECRUIT_DURATION_BUFFER,
  poll: ESTIMATED_SECONDS_PER_POLL * RECRUIT_DURATION_BUFFER,
  image: ESTIMATED_SECONDS_PER_IMAGE * RECRUIT_DURATION_BUFFER,
} as const;

/**
 * How long Remesh will estimate this study at, in whole seconds — the guide's questions and the
 * images shown before them, plus the screeners answered before all of it, with the recruit buffer
 * added and rounded to the second.
 *
 * This is the one duration function: the price, the Guide step, the Launch step and the agent's
 * summary of the study all read it, so no two screens can state a different length for the same
 * guide. Whole seconds are what a quote is priced on, because they cross the wire exactly where a
 * fractional minute would not. It lives here rather than beside the price because the SPA reads it
 * too, and the SPA depends on this package alone.
 */
export const estimateGuideDurationSeconds = (
  guide: DgContent,
  // Required rather than defaulted. A default here would be a number this function
  // guessed, and guessing one screener is exactly the undercount the screener count
  // exists to prevent — a caller without an audience to count does not have enough to
  // price a study, and should not be handed a price anyway.
  screenerPolls: number,
): number => {
  const questions = guide.sections.flatMap((section) => section.questions);
  const asks = questions.filter((question) => question.type === 'open_response').length;
  const polls = questions.length - asks;
  const images = questions.filter((question) => question.stimulus !== undefined).length;

  const seconds =
    asks * ESTIMATED_SECONDS_PER_ASK +
    polls * ESTIMATED_SECONDS_PER_POLL +
    images * ESTIMATED_SECONDS_PER_IMAGE +
    screenerPolls * ESTIMATED_SECONDS_PER_SCREENER_POLL;

  return Math.round(seconds * RECRUIT_DURATION_BUFFER);
};

/** `estimateGuideDurationSeconds` in minutes, for the screens that state a length. */
export const estimateGuideDurationMinutes = (guide: DgContent, screenerPolls: number): number =>
  estimateGuideDurationSeconds(guide, screenerPolls) / 60;

/**
 * The minutes a guide asks of each participant, with no screeners: what the Guide and Launch
 * steps show. Screener time belongs to the audience, which does not exist while the guide is
 * being written, so the guide's own number leaves it out rather than guessing it; the price
 * adds it once there is an audience to count.
 */
export const guideMinutes = (guide: DgContent): number => estimateGuideDurationMinutes(guide, 0);

/**
 * Minutes as a person reads them: the nearest whole minute, and never less than one. A
 * placeholder rounding rule.
 */
export const displayMinutes = (minutes: number): number => Math.max(1, Math.round(minutes));

/**
 * Seconds as minutes to the nearest tenth, for stating what one change costs: finer than
 * `displayMinutes`, which rounds a poll's few seconds away.
 */
export const tenthsOfMinutes = (seconds: number): number => Math.round(seconds / 6) / 10;
